/**
 * Tenants Management Page
 * Full CRUD for tenant management with list view and modals
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import { tenantService, complianceService, arrearsService } from '../../services/api';
import NewTenancyWizard from './NewTenancyWizard';
import GenerateNoticeModal from './GenerateNoticeModal';
import SendReminderModal from './SendReminderModal';
import EditTenantModal from './EditTenantModal';
import CSVImportModal from '../Shared/CSVImportModal';
import SafeguardingPanel from '../RSL/SafeguardingPanel';
import AwaabPanel from '../Awaab/AwaabPanel';
import { addressToText } from '../../utils/formatAddress';

interface Tenant {
    id: string;
    name: string;
    email: string;
    phone?: string;
    propertyId: string;
    tenancyStartDate?: string;
    tenancyEndDate?: string;
    rentAmount?: number;
    rentFrequency?: string;
    status?: string;
    createdAt?: string;
    isLeadTenant?: boolean;
    jointTenancyId?: string | null;
}

interface Property {
    id: string;
    address: string;
    postcode?: string;
}

/**
 * Payment status badge — shown in the Tenant list "Payment" column.
 * Driven by the arrearsService.getTenants() payload (loaded in the background).
 * Falls back to a neutral "Loading…" pill while the call is in flight.
 */
function PaymentStatusBadge({ info }: { info?: {
    status: 'PAID' | 'PARTIAL' | 'OVERDUE' | 'NOT_DUE' | 'UNKNOWN';
    arrears: number;
    rentAmount: number;
    lastPaymentDate?: string;
    lastPaymentAmount?: number;
    paymentRef?: string;
} }) {
    if (!info) {
        return <span className="inline-flex items-center text-xs text-gray-400"><i className="fas fa-spinner fa-spin mr-1"></i>Loading…</span>;
    }

    const STYLES: Record<string, { bg: string; text: string; ring: string; icon: string; label: string }> = {
        PAID:    { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', icon: 'fa-check-circle',          label: 'Paid'         },
        PARTIAL: { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200',   icon: 'fa-clock',                  label: 'Partial'      },
        OVERDUE: { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200',    icon: 'fa-exclamation-circle',     label: 'Overdue'      },
        NOT_DUE: { bg: 'bg-blue-50',    text: 'text-blue-700',    ring: 'ring-blue-200',    icon: 'fa-calendar',               label: 'Not due yet'  },
        UNKNOWN: { bg: 'bg-gray-50',    text: 'text-gray-600',    ring: 'ring-gray-200',    icon: 'fa-question-circle',        label: 'No data'      },
    };
    const s = STYLES[info.status] || STYLES.UNKNOWN;

    const fmtDate = (iso?: string) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';
    const fmtAmt  = (n?: number)   => n != null ? `£${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '';

    return (
        <div className="flex flex-col gap-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${s.bg} ${s.text} ${s.ring} w-fit`}>
                <i className={`fas ${s.icon} mr-1.5`}></i>{s.label}
            </span>
            {info.status === 'OVERDUE' && info.arrears > 0 && (
                <span className="text-[10px] text-rose-700 font-bold">{fmtAmt(info.arrears)} owed</span>
            )}
            {info.lastPaymentDate && (
                <span className="text-[10px] text-gray-500" title={`Last payment: ${fmtAmt(info.lastPaymentAmount)} on ${info.lastPaymentDate}`}>
                    Last: {fmtAmt(info.lastPaymentAmount)} · {fmtDate(info.lastPaymentDate)}
                </span>
            )}
            {info.paymentRef && (
                <span className="text-[10px] text-gray-400 font-mono" title="Payment reference (give this to the tenant for bank transfers)">
                    {info.paymentRef}
                </span>
            )}
        </div>
    );
}

export default function Tenants() {
    const navigate = useNavigate();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [properties, setProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [showNoticeModal, setShowNoticeModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedTenantForNotice, setSelectedTenantForNotice] = useState<Tenant | null>(null);
    const [selectedTenantForReminder, setSelectedTenantForReminder] = useState<Tenant | null>(null);
    const [selectedTenantForEdit, setSelectedTenantForEdit] = useState<Tenant | null>(null);
    const [showSafeguardingModal, setShowSafeguardingModal] = useState(false);
    const [selectedTenantForSafeguarding, setSelectedTenantForSafeguarding] = useState<Tenant | null>(null);
    const [showAwaabModal, setShowAwaabModal] = useState(false);
    const [selectedTenantForAwaab, setSelectedTenantForAwaab] = useState<Tenant | null>(null);
    const [arrearsSet, setArrearsSet] = useState<Set<string>>(new Set());
    // Per-tenant payment summary keyed by tenantId — drives the Payment Status column
    const [paymentStatusMap, setPaymentStatusMap] = useState<Record<string, {
        status: 'PAID' | 'PARTIAL' | 'OVERDUE' | 'NOT_DUE' | 'UNKNOWN';
        arrears: number;
        rentAmount: number;
        lastPaymentDate?: string;
        lastPaymentAmount?: number;
        paymentRef?: string;
    }>>({});
    const [showImportModal, setShowImportModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    const toggleGroup = (propertyId: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.has(propertyId) ? next.delete(propertyId) : next.add(propertyId);
            return next;
        });
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Fast path: tenants + properties only. Arrears is loaded separately
            // in the background so the page renders immediately even on large
            // portfolios (the arrears endpoint iterates every tenant).
            const [tenantsRes, propertiesRes] = await Promise.all([
                tenantService.getTenants(),
                complianceService.getProperties()
            ]);

            const tenantData = tenantsRes.data?.data || tenantsRes.data || [];
            const propertyData = propertiesRes.data?.data || propertiesRes.data || [];

            setTenants(Array.isArray(tenantData) ? tenantData : []);
            setProperties(Array.isArray(propertyData) ? propertyData : []);

            // Background: arrears + payment status populate progressively (non-blocking)
            arrearsService.getTenants()
                .then((arrearsRes: any) => {
                    const arrearsDetails: any[] = arrearsRes?.data?.data || [];
                    const inArrears = new Set<string>();
                    const statusMap: Record<string, any> = {};

                    arrearsDetails.forEach((d: any) => {
                        // Only flag as in-arrears when the tenant has a
                        // currently-overdue balance. UPCOMING / SCHEDULED
                        // tenants with rent not yet due — and ON_TIME / paid
                        // tenants — must NOT be tagged "arrears", even if
                        // the engine reports a residual £x left from a
                        // partial pre-payment of next month.
                        const owesNow =
                            d.status === 'CRITICAL' ||
                            d.status === 'OVERDUE'  ||
                            d.status === 'LATE'     ||
                            d.status === 'PARTIAL';
                        if (owesNow && (d.summary?.arrears || d.arrears || 0) > 0) {
                            inArrears.add(d.tenantId);
                        }

                        // Map arrears engine status → simple payment status column
                        let paymentStatus: 'PAID' | 'PARTIAL' | 'OVERDUE' | 'NOT_DUE' | 'UNKNOWN' = 'UNKNOWN';
                        if (d.status === 'CRITICAL' || d.status === 'OVERDUE')      paymentStatus = 'OVERDUE';
                        else if (d.status === 'PARTIAL')                            paymentStatus = 'PARTIAL';
                        else if (d.status === 'LATE' || d.status === 'PAID_LATE')   paymentStatus = 'PARTIAL';
                        else if (d.status === 'ON_TIME')                            paymentStatus = 'PAID';
                        else if (d.status === 'UPCOMING' || d.status === 'SCHEDULED') paymentStatus = 'NOT_DUE';

                        // Find the most recent payment from the schedule for last-paid info
                        const lastPaid = (d.schedule || [])
                            .flatMap((s: any) => s.payments || [])
                            .sort((a: any, b: any) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime())[0];

                        statusMap[d.tenantId] = {
                            status: paymentStatus,
                            arrears: d.summary?.arrears || d.arrears || 0,
                            rentAmount: d.rentAmount || 0,
                            lastPaymentDate: lastPaid?.paymentDate,
                            lastPaymentAmount: lastPaid?.amount,
                            paymentRef: d.paymentRef, // populated once backend issues these
                        };
                    });

                    setArrearsSet(inArrears);
                    setPaymentStatusMap(statusMap);
                })
                .catch(() => { /* non-fatal — page renders without arrears badges */ });

            console.log('[Tenants] Fetched:', tenantData.length, 'tenants (arrears loading in background)');
        } catch (err: any) {
            console.error('[Tenants] Fetch error:', err);
            setError(err.response?.data?.message || 'Failed to load tenants');
        } finally {
            setLoading(false);
        }
    };

    const getPropertyAddress = (propertyId: string) => {
        if (!propertyId) return 'Unassigned';
        // Use loose equality to handle string/number mismatches from API
        const property = properties.find(p => String(p.id) === String(propertyId));
        if (!property) return 'Unknown Property';
        
        // Handle address as object {line1, city, postcode} or plain string
        const addr = property.address as any;
        if (typeof addr === 'object' && addr?.line1) {
            const parts = [addr.line1];
            if (addr.city) parts.push(addr.city);
            if (addr.postcode) parts.push(`(${addr.postcode})`);
            return parts.join(' ');
        }
        return String(addr || property.postcode || 'Unknown Address');
    };

    const handleDelete = async (tenantId: string) => {
        if (!window.confirm('Are you sure you want to remove this tenant?')) return;

        setDeletingId(tenantId);
        try {
            await tenantService.deleteTenant(tenantId);
            setTenants(prev => prev.filter(t => t.id !== tenantId));
        } catch (err: any) {
            console.error('[Tenants] Delete error:', err);
            alert(err.response?.data?.message || 'Failed to delete tenant');
        } finally {
            setDeletingId(null);
        }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatCurrency = (amount?: number) => {
        if (!amount) return '-';
        return `£${amount.toLocaleString()}`;
    };

    const handleGenerateNotice = (tenant: Tenant) => {
        setSelectedTenantForNotice(tenant);
        setShowNoticeModal(true);
    };

    const handleSendReminder = (tenant: Tenant) => {
        setSelectedTenantForReminder(tenant);
        setShowReminderModal(true);
    };

    const handleEditTenant = (tenant: Tenant) => {
        setSelectedTenantForEdit(tenant);
        setShowEditModal(true);
    };

    const handleSafeguarding = (tenant: Tenant) => {
        setSelectedTenantForSafeguarding(tenant);
        setShowSafeguardingModal(true);
    };

    const handleAwaab = (tenant: Tenant) => {
        setSelectedTenantForAwaab(tenant);
        setShowAwaabModal(true);
    };

    const filteredTenants = tenants.filter(t => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            t.name?.toLowerCase().includes(searchLower) ||
            t.email?.toLowerCase().includes(searchLower) ||
            t.phone?.toLowerCase().includes(searchLower) ||
            getPropertyAddress(t.propertyId).toLowerCase().includes(searchLower)
        );
    });

    // Group tenants by property — distinguishing joint tenancies from HMO.
    // Joint tenants share a jointTenancyId. HMO grouping is decided by the
    // PROPERTY type, NOT by tenant count, so a residential with one ended
    // tenancy + one new active tenancy does not get rendered as HMO.
    // Look up property type from the properties array we already fetched.
    const propertyTypeById: Record<string, string> = {};
    properties.forEach((p: any) => { propertyTypeById[p.id] = (p.type || '').toString(); });

    const groupedTenants: { propertyId: string; address: string; tenants: Tenant[]; isJoint: boolean; isHMOProperty: boolean }[] = [];
    const seenProps: Record<string, number> = {};
    filteredTenants.forEach(t => {
        // Joint additional tenants are nested under their lead — skip them at the top level
        if (t.jointTenancyId && !t.isLeadTenant) return;
        // Ended / former tenancies don't define the property's current
        // occupancy shape. Show them as their own row, never bundled into
        // an HMO-style header alongside a new active tenancy.
        const status = (t.status || 'active').toLowerCase();
        const isEnded = status === 'ended' || status === 'former' || status === 'inactive';

        const propType = propertyTypeById[t.propertyId || ''] || '';
        const propertyIsHMO = /hmo/i.test(propType);

        // Ended tenancies on non-HMO properties always live in their own
        // row; never merge them with an active tenancy on the same residential.
        const key = isEnded && !propertyIsHMO
            ? `${t.propertyId || '__unassigned__'}__ended__${t.id}`
            : (t.propertyId || '__unassigned__');
        if (seenProps[key] === undefined) {
            seenProps[key] = groupedTenants.length;
            groupedTenants.push({
                propertyId: key,
                address: getPropertyAddress(t.propertyId),
                tenants: [t],
                isJoint: false,
                isHMOProperty: propertyIsHMO,
            });
        } else {
            groupedTenants[seenProps[key]].tenants.push(t);
        }
    });

    // For each group, attach joint tenants to their lead
    const jointByLeadId: Record<string, Tenant[]> = {};
    filteredTenants.filter(t => t.jointTenancyId && !t.isLeadTenant).forEach(t => {
        const leadId = t.jointTenancyId!;
        if (!jointByLeadId[leadId]) jointByLeadId[leadId] = [];
        jointByLeadId[leadId].push(t);
    });

    // Flag groups that are actual joint tenancies (2 tenants via jointTenancyId)
    groupedTenants.forEach(g => {
        const hasJointKids = g.tenants.some(t => jointByLeadId[t.id]?.length > 0);
        if (hasJointKids) g.isJoint = true;
    });

    const getStatusBadge = (status?: string, endDate?: string) => {
        // Check if tenancy has ended
        if (endDate && new Date(endDate) < new Date()) {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Ended</span>;
        }

        const statusColors: Record<string, string> = {
            active: 'bg-green-100 text-green-800',
            pending: 'bg-yellow-100 text-yellow-800',
            pending_activation: 'bg-amber-100 text-amber-800',
            ended: 'bg-gray-100 text-gray-800'
        };

        const statusLabels: Record<string, string> = {
            active: 'Active',
            pending: 'Pending',
            pending_activation: 'Pending Activation',
            ended: 'Ended',
        };

        const key = status || 'active';
        return (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[key] || statusColors.active}`}>
                {statusLabels[key] || (key.charAt(0).toUpperCase() + key.slice(1))}
            </span>
        );
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <Navbar />

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="md:flex md:items-center md:justify-between mb-8">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                            <i className="fas fa-users text-emerald-500 mr-3"></i>
                            Tenant Management
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Track tenant details, tenancy agreements, and rental information.
                        </p>
                    </div>
                    <div className="mt-4 flex flex-col sm:flex-row md:mt-0 md:ml-4 space-y-2 sm:space-y-0 sm:space-x-2">
                        <button
                            onClick={() => setShowImportModal(true)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                        >
                            <i className="fas fa-file-import mr-2"></i>
                            Bulk Import
                        </button>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                        >
                            <i className="fas fa-plus mr-2"></i>
                            Add Tenant
                        </button>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="text-center py-20">
                        <div className="animate-spin h-12 w-12 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-gray-500">Loading tenants...</p>
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                        <i className="fas fa-exclamation-circle text-red-500 text-3xl mb-3"></i>
                        <p className="text-red-700">{error}</p>
                        <button onClick={fetchData} className="mt-3 text-sm text-red-600 hover:text-red-800">
                            Try again
                        </button>
                    </div>
                )}

                {/* Empty State */}
                {!loading && !error && tenants.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
                        <div className="mx-auto h-24 w-24 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                            <i className="fas fa-users text-4xl text-emerald-600"></i>
                        </div>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">No tenants yet</h3>
                        <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
                            Start managing your tenants by adding them to your properties.
                            Keep track of tenancy dates, rent, and contact information.
                        </p>
                        <div className="mt-6">
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700"
                            >
                                <i className="fas fa-plus mr-2"></i>
                                Add Your First Tenant
                            </button>
                        </div>
                    </div>
                )}

                {/* Tenant List */}
                {!loading && !error && tenants.length > 0 && (
                    <div className="bg-white shadow rounded-lg overflow-x-auto">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
                            <p className="text-sm text-gray-600">
                                <i className="fas fa-info-circle mr-2"></i>
                                {filteredTenants.length} tenant{filteredTenants.length !== 1 ? 's' : ''} across {groupedTenants.length} propert{groupedTenants.length !== 1 ? 'ies' : 'y'}
                            </p>
                            <div className="relative w-full sm:w-64">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <i className="fas fa-search text-gray-400"></i>
                                </span>
                                <input
                                    type="text"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                                    placeholder="Search tenants or property..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenancy</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rent</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky right-0">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {groupedTenants.map(group => {
                                    // HMO grouping comes from the property type ONLY.
                                    // Tenant count is no longer a proxy — a residential
                                    // with an ended + a new active tenancy must stay
                                    // residential.
                                    const isHMO = group.isHMOProperty && group.tenants.length > 1;
                                    const isExpanded = expandedGroups.has(group.propertyId);

                                    const TenantRow = ({ tenant, indent, jointRole }: { tenant: Tenant; indent?: boolean; jointRole?: 'lead' | 'joint' }) => (
                                        <tr key={tenant.id} className={`hover:bg-gray-50 ${indent ? 'bg-gray-50/50' : ''}`}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className={`flex items-center ${indent ? 'ml-6' : ''}`}>
                                                    {indent && <div className="w-4 border-l-2 border-b-2 border-gray-200 h-4 mr-2 rounded-bl"></div>}
                                                    <div className="h-9 w-9 flex-shrink-0 bg-emerald-100 rounded-full flex items-center justify-center">
                                                        <span className="text-emerald-600 font-medium text-sm">{tenant.name?.charAt(0)?.toUpperCase() || '?'}</span>
                                                    </div>
                                                    <div className="ml-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-gray-900">{tenant.name}</span>
                                                            {arrearsSet.has(tenant.id) && (
                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-200" title="In arrears">
                                                                    £ ARREARS
                                                                </span>
                                                            )}
                                                            {jointRole === 'lead' && (
                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700">Lead</span>
                                                            )}
                                                            {jointRole === 'joint' && (
                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700">Joint</span>
                                                            )}
                                                        </div>
                                                        <div className="text-sm text-gray-500">{tenant.email}</div>
                                                        {tenant.phone && <div className="text-xs text-gray-400">{tenant.phone}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {indent ? <span className="text-xs text-gray-400 italic">↳ same property</span> : (
                                                    <button onClick={() => navigate(`/properties/${tenant.propertyId}`)} className="text-sm text-emerald-600 hover:text-emerald-800 hover:underline">
                                                        {getPropertyAddress(tenant.propertyId)}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div>{formatDate(tenant.tenancyStartDate)}</div>
                                                <div className="text-xs text-gray-400">to {formatDate(tenant.tenancyEndDate)}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <div>{formatCurrency(tenant.rentAmount)}</div>
                                                <div className="text-xs text-gray-400">{tenant.rentFrequency || 'monthly'}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <PaymentStatusBadge info={paymentStatusMap[tenant.id]} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(tenant.status, tenant.tenancyEndDate)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium bg-white sticky right-0">
                                                <button onClick={() => handleGenerateNotice(tenant)} className="text-indigo-600 hover:text-indigo-800 mr-3" title="Generate Notice"><i className="fas fa-file-contract"></i></button>
                                                <button onClick={() => handleSendReminder(tenant)} className="text-orange-600 hover:text-orange-800 mr-3" title="Send Reminder"><i className="fas fa-paper-plane"></i></button>
                                                <button onClick={() => handleSafeguarding(tenant)} className="text-rose-600 hover:text-rose-800 mr-3" title="Safeguarding & Pre-Action Protocol"><i className="fas fa-shield-halved"></i></button>
                                                <button onClick={() => handleAwaab(tenant)} className="text-orange-600 hover:text-orange-800 mr-3" title="Awaab's Law Hazard Management"><i className="fas fa-house-chimney-crack"></i></button>
                                                <button onClick={() => handleEditTenant(tenant)} className="text-blue-600 hover:text-blue-800 mr-3" title="Edit Tenant"><i className="fas fa-pencil-alt"></i></button>
                                                <button onClick={() => navigate(`/agreements?propertyId=${tenant.propertyId}`)} className="text-emerald-600 hover:text-emerald-800 mr-3" title="View Agreements"><i className="fas fa-file-contract"></i></button>
                                                <button onClick={() => handleDelete(tenant.id)} disabled={deletingId === tenant.id} className="text-red-600 hover:text-red-800 disabled:opacity-50">
                                                    {deletingId === tenant.id ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash"></i>}
                                                </button>
                                            </td>
                                        </tr>
                                    );

                                    // Single tenant — no grouping needed
                                    if (!isHMO && !group.isJoint) return <TenantRow key={group.propertyId} tenant={group.tenants[0]} />;

                                    // Joint tenancy — lead + additional tenants linked by jointTenancyId
                                    if (group.isJoint) {
                                        return (
                                            <React.Fragment key={group.propertyId}>
                                                {group.tenants.map(leadTenant => (
                                                    <React.Fragment key={leadTenant.id}>
                                                        <tr className="bg-indigo-50 border-l-4 border-indigo-400 cursor-pointer hover:bg-indigo-100" onClick={() => toggleGroup(leadTenant.id)}>
                                                            <td className="px-6 py-3 whitespace-nowrap" colSpan={2}>
                                                                <div className="flex items-center">
                                                                    <i className={`fas fa-chevron-${expandedGroups.has(leadTenant.id) ? 'down' : 'right'} text-indigo-500 mr-3 text-xs`}></i>
                                                                    <div className="h-8 w-8 flex-shrink-0 bg-indigo-600 rounded-lg flex items-center justify-center mr-3">
                                                                        <i className="fas fa-user-friends text-white text-xs"></i>
                                                                    </div>
                                                                    <div>
                                                                        <div className="text-sm font-bold text-indigo-800">{leadTenant.name}</div>
                                                                        <div className="text-xs text-indigo-600">
                                                                            Joint Tenancy · {(jointByLeadId[leadTenant.id]?.length || 0) + 1} tenants · {getPropertyAddress(leadTenant.propertyId)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-3 text-xs text-indigo-500" colSpan={5}>
                                                                Click to {expandedGroups.has(leadTenant.id) ? 'collapse' : 'expand'} joint tenants
                                                            </td>
                                                        </tr>
                                                        {expandedGroups.has(leadTenant.id) && (
                                                            <>
                                                                <TenantRow tenant={leadTenant} indent jointRole="lead" />
                                                                {(jointByLeadId[leadTenant.id] || []).map(jt => (
                                                                    <TenantRow key={jt.id} tenant={jt} indent jointRole="joint" />
                                                                ))}
                                                            </>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </React.Fragment>
                                        );
                                    }

                                    // HMO group — show collapsible header + nested tenants
                                    return (
                                        <React.Fragment key={group.propertyId}>
                                            {/* HMO property header row */}
                                            <tr className="bg-emerald-50 border-l-4 border-emerald-500 cursor-pointer hover:bg-emerald-100" onClick={() => toggleGroup(group.propertyId)}>
                                                <td className="px-6 py-3 whitespace-nowrap" colSpan={2}>
                                                    <div className="flex items-center">
                                                        <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-emerald-600 mr-3 text-xs`}></i>
                                                        <div className="h-8 w-8 flex-shrink-0 bg-emerald-600 rounded-lg flex items-center justify-center mr-3">
                                                            <i className="fas fa-building text-white text-xs"></i>
                                                        </div>
                                                        <div>
                                                            <button onClick={(e) => { e.stopPropagation(); navigate(`/properties/${group.propertyId}`); }} className="text-sm font-bold text-emerald-700 hover:underline">{addressToText(group.address)}</button>
                                                            <div className="text-xs text-emerald-600">{group.tenants.length} tenants · HMO</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 text-xs text-emerald-600" colSpan={5}>
                                                    Click to {isExpanded ? 'collapse' : 'expand'} {group.tenants.length} tenants
                                                </td>
                                            </tr>
                                            {/* Individual tenant rows — shown when expanded */}
                                            {isExpanded && group.tenants.map(t => <TenantRow key={t.id} tenant={t} indent />)}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Footer />

            {/* Modals */}
            <NewTenancyWizard
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={fetchData}
            />

            {/* Generate Notice Modal */}
            <GenerateNoticeModal
                isOpen={showNoticeModal}
                onClose={() => {
                    setShowNoticeModal(false);
                    setSelectedTenantForNotice(null);
                }}
                tenant={selectedTenantForNotice}
            />

            {/* Send Reminder Modal */}
            <SendReminderModal
                isOpen={showReminderModal}
                onClose={() => {
                    setShowReminderModal(false);
                    setSelectedTenantForReminder(null);
                }}
                tenant={selectedTenantForReminder}
            />

            {/* Edit Tenant Modal - B-025 */}
            <EditTenantModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedTenantForEdit(null);
                }}
                onSuccess={fetchData}
                tenant={selectedTenantForEdit}
            />

            {/* RSL Safeguarding & Pre-Action Protocol (E-017 / F-047) */}
            <SafeguardingPanel
                isOpen={showSafeguardingModal}
                onClose={() => {
                    setShowSafeguardingModal(false);
                    setSelectedTenantForSafeguarding(null);
                }}
                tenant={selectedTenantForSafeguarding}
            />

            {/* Awaab's Law Hazard Management (E-018 / F-049 + F-050 + F-051) */}
            <AwaabPanel
                isOpen={showAwaabModal}
                onClose={() => {
                    setShowAwaabModal(false);
                    setSelectedTenantForAwaab(null);
                }}
                tenant={selectedTenantForAwaab}
            />

            <CSVImportModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImport={async (data) => {
                    await tenantService.bulkImport(data);
                    fetchData();
                }}
                title="Bulk Import Tenants"
                headerRequirements={[
                    { id: 'name', label: 'Full Name', required: true, description: 'Legal name of the tenant', example: 'John Smith' },
                    { id: 'email', label: 'Email Address', required: true, description: 'Direct contact email', example: 'john@example.com' },
                    { id: 'propertyAddress', label: 'Property Address', required: false, description: 'Matches tenant to asset by address (Recommended)', example: '123 High St' },
                    { id: 'propertyId', label: 'Property ID', required: false, description: 'Internal ID (optional if address provided)', example: 'prop_123' },
                    { id: 'tenancyStartDate', label: 'Start Date', required: false, description: 'Date format YYYY-MM-DD', example: '2024-01-01' },
                    { id: 'rentAmount', label: 'Rent Amount', required: false, description: 'Numeric monthly rent value', example: '1200' }
                ]}
                templateHeaders={['name', 'email', 'phone', 'propertyAddress', 'propertyId', 'tenancyStartDate', 'rentAmount']}
                sampleData="John Doe,john@example.com,0712345678,123 High St,prop_123,2024-01-01,1200"
            />
        </div>
    );
}
